import jsQR from 'jsqr'

export default {
  data() {
    return {
      canUseCameraScanner: typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      isPhoneScanning: false,
      isStartingCamera: false,
      phoneScannerStream: null,
      phoneScannerDetector: null,
      phoneScannerFrameId: null,
      phoneScannerCanvas: null,
      phoneScannerCanvasContext: null,
      isProcessingPhoneScan: false,
      lastScannedRef: '',
      lastScannedAt: 0,
    }
  },
  beforeDestroy() {
    this.stopPhoneScanner()
  },
  methods: {
    async togglePhoneScanner() {
      if (this.isPhoneScanning || this.isStartingCamera) {
        this.stopPhoneScanner()
        return
      }
      await this.startPhoneScanner()
    },
    async startPhoneScanner() {
      if (!this.canUseCameraScanner) return
      this.isStartingCamera = true
      try {
        this.phoneScannerDetector = this.createBarcodeDetector()
        this.phoneScannerCanvas = document.createElement('canvas')
        this.phoneScannerCanvasContext = this.phoneScannerCanvas.getContext('2d', { willReadFrequently: true })
        this.phoneScannerStream = await this.openCameraStream()
        this.isPhoneScanning = true
        await this.$nextTick()
        const video = this.$refs.phoneScannerVideo
        video.srcObject = this.phoneScannerStream
        await video.play()
        this.scanPhoneCameraFrame()
      } catch (error) {
        this.notifyPhoneScannerError(this.getCameraErrorMessage(error))
        this.stopPhoneScanner()
      } finally {
        this.isStartingCamera = false
      }
    },
    async openCameraStream() {
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: 'environment' } },
          audio: false,
        })
      } catch (error) {
        if (error && (error.name === 'OverconstrainedError' || error.name === 'NotFoundError')) {
          return navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
          })
        }
        throw error
      }
    },
    getCameraErrorMessage(error) {
      if (!error) return 'Impossible d’ouvrir la caméra pour scanner le code'
      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        return 'Autorisez l’accès à la caméra dans le navigateur, puis réessayez'
      }
      if (error.name === 'NotFoundError') return 'Aucune caméra disponible sur cet appareil'
      if (error.name === 'NotReadableError') return 'La caméra est déjà utilisée par une autre application'
      return 'Impossible d’ouvrir la caméra pour scanner le code'
    },
    createBarcodeDetector() {
      if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null
      try {
        return new window.BarcodeDetector({
          formats: ['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'itf', 'codabar', 'upc_a', 'upc_e'],
        })
      } catch (error) {
        try {
          return new window.BarcodeDetector({ formats: ['qr_code'] })
        } catch (detectorError) {
          return null
        }
      }
    },
    stopPhoneScanner() {
      if (this.phoneScannerFrameId) {
        window.cancelAnimationFrame(this.phoneScannerFrameId)
        this.phoneScannerFrameId = null
      }
      if (this.phoneScannerStream) {
        this.phoneScannerStream.getTracks().forEach(track => track.stop())
        this.phoneScannerStream = null
      }
      const video = this.$refs.phoneScannerVideo
      if (video) video.srcObject = null
      this.phoneScannerCanvas = null
      this.phoneScannerCanvasContext = null
      this.phoneScannerDetector = null
      this.isPhoneScanning = false
    },
    async scanPhoneCameraFrame() {
      if (!this.isPhoneScanning || !this.$refs.phoneScannerVideo) return
      try {
        const codes = this.phoneScannerDetector
          ? await this.phoneScannerDetector.detect(this.$refs.phoneScannerVideo)
          : []
        if (codes.length && codes[0].rawValue) {
          await this.handlePhoneScan(codes[0].rawValue)
        } else {
          const decodedValue = this.decodeQrFromVideoFrame()
          if (decodedValue) await this.handlePhoneScan(decodedValue)
        }
      } catch (error) {
        this.notifyPhoneScannerError('Erreur pendant la lecture du code')
        this.stopPhoneScanner()
        return
      }
      this.phoneScannerFrameId = window.requestAnimationFrame(this.scanPhoneCameraFrame)
    },
    decodeQrFromVideoFrame() {
      const video = this.$refs.phoneScannerVideo
      if (!video || !this.phoneScannerCanvas || !this.phoneScannerCanvasContext) return ''
      if (!video.videoWidth || !video.videoHeight) return ''
      this.phoneScannerCanvas.width = video.videoWidth
      this.phoneScannerCanvas.height = video.videoHeight
      this.phoneScannerCanvasContext.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const imageData = this.phoneScannerCanvasContext.getImageData(0, 0, video.videoWidth, video.videoHeight)
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' })
      return code && code.data ? code.data : ''
    },
    async handlePhoneScan(rawValue) {
      const scannedRef = this.extractOrderReference(rawValue)
      if (!scannedRef || this.isProcessingPhoneScan) return
      const now = Date.now()
      if (this.lastScannedRef === scannedRef && now - this.lastScannedAt < 2000) {
        this.lastScannedAt = now
        return
      }
      this.lastScannedRef = scannedRef
      this.lastScannedAt = now
      this.isProcessingPhoneScan = true
      try {
        await this.onPhoneReferenceScanned(scannedRef)
      } finally {
        this.isProcessingPhoneScan = false
      }
    },
    extractOrderReference(rawValue) {
      const value = rawValue ? rawValue.toString().trim() : ''
      if (!value) return ''
      try {
        const url = new URL(value)
        const queryRef = url.searchParams.get('order_id')
          || url.searchParams.get('order')
          || url.searchParams.get('id')
          || url.searchParams.get('ref')
          || url.searchParams.get('barcode')
        if (queryRef) return queryRef.trim()
        const pathParts = url.pathname.split('/').filter(Boolean)
        if (pathParts.length) return pathParts[pathParts.length - 1].trim()
      } catch (error) {
        // Printed labels may contain a plain BL or universal barcode.
      }
      return value
    },
    notifyPhoneScannerError(message) {
      if (typeof this.showToast === 'function') {
        this.showToast('danger', 'top-center', message)
      } else {
        this.error = message
      }
    },
  },
}
